import { CrudPage } from '@/components/CrudPage';
import { Badge, Card } from '@/components/ui';
import { formatRelative } from '@/lib/format';

interface ClientUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  phone: string | null;
  last_login_at: string | null;
}

const ROLE_TONES: Record<string, 'purple' | 'blue' | 'gray'> = {
  CLIENT_ADMIN: 'purple',
  CLIENT_MANAGER: 'blue',
  CLIENT_AGENT: 'gray',
  VIEWER: 'gray',
};

const ROLE_DESCRIPTIONS: Array<[string, string]> = [
  ['Client admin', 'Full access, including users, billing and bot configuration.'],
  ['Client manager', 'Day-to-day operations: bots, assignments, leads and reports.'],
  ['Client agent', 'Read-only across the portal, plus adding numbers to DNC.'],
  ['Viewer', 'Read-only. Cannot change anything.'],
];

export default function TeamUsers() {
  return (
    <CrudPage<ClientUser>
      title="Users"
      description="People in your organisation who can sign in to this portal."
      resource="client_users"
      path="/users"
      queryKey="client-users"
      searchPlaceholder="Search by name or email…"
      emptyTitle="No users yet"
      createLabel="Invite user"
      filters={[
        {
          key: 'role',
          label: 'Role',
          options: Object.keys(ROLE_TONES).map((value) => ({
            value,
            label: value.replace(/_/g, ' '),
          })),
        },
        {
          key: 'status',
          label: 'Status',
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INVITED', label: 'Invited' },
            { value: 'DISABLED', label: 'Disabled' },
          ],
        },
      ]}
      fields={[
        {
          name: 'email',
          label: 'Email',
          type: 'email',
          required: true,
          immutable: true,
          hint: 'This is how they sign in.',
        },
        { name: 'name', label: 'Full name', required: true },
        {
          name: 'role',
          label: 'Role',
          type: 'select',
          required: true,
          defaultValue: 'CLIENT_AGENT',
          options: Object.keys(ROLE_TONES).map((value) => ({
            value,
            label: value.replace(/_/g, ' '),
          })),
        },
        { name: 'phone', label: 'Phone' },
        {
          name: 'password',
          label: 'Password',
          type: 'password',
          hint: 'At least 12 characters. Leave blank to create an invited user who sets their own.',
        },
      ]}
      columns={[
        {
          key: 'name',
          header: 'User',
          render: (user) => (
            <div>
              <div className="font-medium text-ink-900 dark:text-ink-50">{user.name}</div>
              <div className="text-xs text-ink-500">{user.email}</div>
            </div>
          ),
        },
        {
          key: 'role',
          header: 'Role',
          render: (user) => (
            <Badge tone={ROLE_TONES[user.role] ?? 'gray'}>{user.role.replace(/_/g, ' ')}</Badge>
          ),
        },
        {
          key: 'status',
          header: 'Status',
          render: (user) => (
            <Badge
              tone={
                user.status === 'ACTIVE' ? 'green' : user.status === 'INVITED' ? 'amber' : 'gray'
              }
            >
              {user.status}
            </Badge>
          ),
        },
        {
          key: 'phone',
          header: 'Phone',
          render: (user) => user.phone ?? <span className="text-ink-400">—</span>,
        },
        {
          key: 'last_login',
          header: 'Last sign-in',
          render: (user) => (
            <span className="text-xs text-ink-500">{formatRelative(user.last_login_at)}</span>
          ),
        },
      ]}
    >
      <Card title="Roles" className="mb-4">
        <dl className="grid gap-3 sm:grid-cols-2">
          {ROLE_DESCRIPTIONS.map(([role, description]) => (
            <div key={role}>
              <dt className="text-sm font-medium text-ink-900 dark:text-ink-50">{role}</dt>
              <dd className="text-xs text-ink-500 dark:text-ink-400">{description}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </CrudPage>
  );
}
